import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '../../commons/services/alert.service';
import { AppState } from '../reducers';

import * as TenantsActions from '../actions/tenants.actions';
import { getSelectionDialogId, getTenantDialogId, getTenantsTableState } from '../selectors/tenant.selectors';
import { NewTenantsComponent } from '../../modules/home/modules/tenants/components/new-tenants/new-tenants.component';
import { LaravelTenantService } from '../../commons/services/laravel/laravel-tenant.service';
import { DialogComponent } from '../../modules/shared/components/dialog/dialog.component';


@Injectable()
export class TenantEffects {

  error$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TenantsActions.deleteTenantFailed,
          TenantsActions.saveTenantFailed
        ),
        tap(({ error }) => {
          if (error) {
            this.alertService.showErrorMessage('Errore', error);
          }
        })
      ),
    { dispatch: false }
  );

  loadTenants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.loadTenants),
      switchMap(({ page, perPage, order, direction, filters, includes }) => {
        return this.tenantService
          .list(page, perPage, order, direction, filters, includes)
          .pipe(
            map((result) =>
              TenantsActions.loadTenantsCompleted({
                  tenants: result.data,
                  currentPage: page,
                  total: result.total,
                  perPage,
                  order,
                  direction,
                  filters,
                  includes
              })
          ),
            catchError((error) =>
              of(TenantsActions.loadTenantsFailed({ error }))
            )
          );
      })
    )
  );


  changePage = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.changePage),
      withLatestFrom(this.store$.select(getTenantsTableState)),
      map(
        ([
          { page, size },
          { total, currentPage, perPage, direction, order, filters, includes },
        ]) =>
          TenantsActions.loadTenants({
            page: page,
            perPage: size,
            order,
            direction,
            filters,
            includes,
          })
      )
    )
  );

  changeSort = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.changeSort),
      withLatestFrom(this.store$.select(getTenantsTableState)),
      map(
        ([
          action,
          { total, currentPage, perPage, direction, order, filters, includes },
        ]) =>
          TenantsActions.loadTenants({
            page: currentPage,
            perPage: perPage,
            order: action.order,
            direction: action.direction,
            filters,
            includes,
          })
      )
    )
  );

  changeFilters = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.changeFilters),
      withLatestFrom(this.store$.select(getTenantsTableState)),
      map(
        ([
          { filters },
          { total, currentPage, perPage, direction, order, includes },
        ]) =>
          TenantsActions.loadTenants({
            page: currentPage,
            perPage: perPage,
            order,
            direction,
            filters,
            includes,
          })
      )
    )
  );

  editTenant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.editTenant),
      map(({ tenant }) => {
        let dialogRef = this.dialog.open(NewTenantsComponent, {
          data: {
            tenant,
          },
          panelClass: 'full-screen-dialog',
        });
        return TenantsActions.tenantDialogOpened({
          dialogId: dialogRef.id,
        });
      })
    )
  );

  saveTenant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.saveTenant),
      mergeMap(({ tenant }) =>
        this.tenantService.upsert(tenant.toDTO()).pipe(
          map((result) =>
            TenantsActions.saveTenantCompleted({ tenant: result })
          ),
          catchError((error) =>
            of(TenantsActions.saveTenantFailed({ error }))
          )
        )
      )
    )
  );

  onSaveCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.saveTenantCompleted),
      map((action) => action.tenant),
      tap((tenant) =>
        this.alertService.showConfirmMessage(
          `${tenant.id} salvata con successo`
        )
      ),
      map(() => TenantsActions.closeTenantsDialog())
    )
  );


  deleteTenant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.deleteTenant),
      switchMap(({ tenant }) =>
        this.alertService
          .showConfirmDialog(
            'Conferma disattivazione',
            `Sei sicuro di voler disattivare il punto vendita?`
          )
          .pipe(
            mergeMap((confirm) => {
              return confirm
                ? this.tenantService.delete(tenant.id).pipe(
                    map(() =>
                      TenantsActions.deleteTenantCompleted({
                        tenant,
                      })
                    ),
                    catchError((error) =>
                      of(TenantsActions.deleteTenantFailed({ error }))
                    )
                  )
                : of(TenantsActions.deleteTenantCancelled());
            })
          )
      )
    )
  );

  onDeleteCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.deleteTenantCompleted),
      tap(({ tenant }) =>
        this.alertService.showConfirmMessage(
          ` ${tenant.id} disattivato con successo`
        )
      ),
      map(() => TenantsActions.closeTenantsDialog())
    )
  );

  closeDialog = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TenantsActions.closeTenantsDialog),
        withLatestFrom(this.store$.select(getTenantDialogId)),
        tap(([_, dialogId]) => {
          if (dialogId) {
            this.dialog.getDialogById(dialogId).close();
          }
        })
      ),
    { dispatch: false }
  );

  reloadAfterSave = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.saveTenantCompleted),
      withLatestFrom(this.store$.select(getTenantsTableState)),
      map(
        ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
          TenantsActions.loadTenants({
            page: currentPage,
            perPage,
            order,
            direction,
            filters,
            includes,
          })
      )
    )
  );

  reloadAfterDelete = createEffect(() =>
    this.actions$.pipe(
      ofType(TenantsActions.deleteTenantCompleted),
      withLatestFrom(this.store$.select(getTenantsTableState)),
      map(
        ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
          TenantsActions.loadTenants({
            page: currentPage,
            perPage,
            order,
            direction,
            filters,
            includes,
          })
      )
    )
  );


  closeSelectionDialog = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TenantsActions.closeSelectionDialog),
        withLatestFrom(this.store$.select(getSelectionDialogId)),
        tap(([_, dialogId]) => {
          if (dialogId) {
            this.dialog.getDialogById(dialogId).close();
          }
        })
      ),
    { dispatch: false }
  );


  requestCloseDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(TenantsActions.requestCloseTenantsDialog),
        switchMap(() => {
          const confirmDialogRef = this.dialog.open(DialogComponent, {
            disableClose: false
          });
    
          // Imposta il messaggio del dialog
          confirmDialogRef.componentInstance.title = "Chiudi";
          confirmDialogRef.componentInstance.confirmMessage =
            "Ci sono modifiche non salvate. Sei sicuro di voler chiudere?";
    
          return confirmDialogRef.afterClosed().pipe(
            map(result =>
              result
                ? TenantsActions.confirmCloseTenantsDialog()
                : TenantsActions.cancelCloseTenantsDialog()
            )
          );
        })
      )
    );
    
    
    confirmCloseDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(TenantsActions.confirmCloseTenantsDialog),
        map(() => TenantsActions.closeTenantsDialog())
      )
    );
    
    cancelCloseDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(TenantsActions.cancelCloseTenantsDialog),
        map(() => {
          // Se l'utente annulla mantiene il dialog aperto
          return { type: 'NO_ACTION' };
        })
      )
    );
    
    closeDialog$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(TenantsActions.closeTenantsDialog),
          withLatestFrom(this.store$.select(getTenantDialogId)),
          tap(([_, dialogId]) => {
            if (dialogId) {
              this.dialog.getDialogById(dialogId).close();
            }
          })
        ),
      { dispatch: false }
    );
      


  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private tenantService: LaravelTenantService,
    private dialog: MatDialog,
    private alertService: AlertService
  ) {}
}
